import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, RotateCcw, Server, Zap, Loader2, ListChecks } from 'lucide-react';
import { Pagination } from '../../components/client';
import { useAdminFailedOperations } from '../../hooks/useAdminFailedOperations';
import { useToastContext } from '../../contexts/ToastContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const RetryButton = ({ onClick, disabled, label = 'Retry' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 !rounded-xl bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
  >
    <RotateCcw className="w-3.5 h-3.5" />
    {label}
  </button>
);

const EmptyRow = ({ message }) => (
  <tr>
    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
      {message}
    </td>
  </tr>
);

const AdminFailedOperations = () => {
  const [activeTab, setActiveTab] = useState('queue');
  const [pages, setPages] = useState({ queue: 1, outbox: 1, stripe: 1 });
  const toast = useToastContext();
  const {
    summary,
    perPage,
    queueJobs,
    queueMeta,
    outboxEvents,
    outboxMeta,
    stripeWebhooks,
    stripeMeta,
    isLoading,
    refetch,
    retryQueueJob,
    retryOutboxEvent,
    retryStripeWebhook,
    retryAllQueueJobs,
    retryAllOutboxEvents,
    retryAllStripeWebhooks,
    isRetrying,
  } = useAdminFailedOperations({
    queuePage: pages.queue,
    outboxPage: pages.outbox,
    stripePage: pages.stripe,
  });

  const activeMeta =
    activeTab === 'queue' ? queueMeta : activeTab === 'outbox' ? outboxMeta : stripeMeta;

  const handlePageChange = (page) => {
    setPages((current) => ({ ...current, [activeTab]: page }));
  };

  const tabs = [
    { id: 'queue', label: 'Queue jobs', count: summary?.queue_failed ?? 0, bulkLimit: 50 },
    { id: 'outbox', label: 'Outbox events', count: summary?.outbox_failed ?? 0, bulkLimit: 50 },
    { id: 'stripe', label: 'Stripe webhooks', count: summary?.stripe_failed ?? 0, bulkLimit: 25 },
  ];

  const activeTabConfig = tabs.find((t) => t.id === activeTab);

  const handleBulkRetry = () => {
    const tab = activeTabConfig;
    if (!tab || tab.count === 0) {
      return;
    }

    const limit = Math.min(tab.bulkLimit, tab.count);
    const confirmed = window.confirm(
      `Retry up to ${limit} failed ${tab.label.toLowerCase()}?`
    );
    if (!confirmed) {
      return;
    }

    const onSuccess = (response) => {
      const stats = response?.data || {};
      const message = response?.message || 'Bulk retry completed';
      if ((stats.failed ?? 0) > 0) {
        toast.error(null, `${message} (${stats.failed} failed)`);
      } else {
        toast.success(null, message);
      }
    };

    const onError = () => {
      toast.error(null, 'Bulk retry failed');
    };

    if (activeTab === 'queue') {
      retryAllQueueJobs.mutate(limit, { onSuccess, onError });
    } else if (activeTab === 'outbox') {
      retryAllOutboxEvents.mutate(limit, { onSuccess, onError });
    } else {
      retryAllStripeWebhooks.mutate(limit, { onSuccess, onError });
    }
  };

  const totalFailed =
    (summary?.queue_failed ?? 0) +
    (summary?.outbox_failed ?? 0) +
    (summary?.stripe_failed ?? 0);

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Failed operations"
        subtitle="Retry failed queue jobs, outbox publishes, and Stripe webhook processing."
        actions={
          <>
            <Link
              to="/admin/performance"
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium !rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Infrastructure overview
            </Link>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-3 py-2.5 border border-gray-300 dark:border-gray-600 !rounded-xl text-sm font-medium bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.failedOperations.title}
        steps={ADMIN_PAGE_GUIDES.failedOperations.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`text-left rounded-2xl border p-4 transition-colors shadow-sm ${
              activeTab === tab.id
                ? 'border-primary-500/60 bg-primary-50/80 dark:bg-primary-950/30 dark:border-primary-700'
                : 'border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <p className="text-sm text-gray-600 dark:text-gray-400">{tab.label}</p>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${tab.count > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-50'}`}>
              {tab.count}
            </p>
          </button>
        ))}
      </div>

      {totalFailed === 0 && !isLoading && (
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/30 p-4 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
          <Server className="w-4 h-4" />
          No failed operations right now.
        </div>
      )}

      <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="inline-flex flex-wrap rounded-full bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
              role="tablist"
              aria-label="Failed operation type"
            >
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {tab.label}
                    <span className={`text-xs tabular-nums ${tab.count > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="hidden sm:flex items-center gap-2 text-gray-500">
              <Zap className="w-4 h-4" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-50">
                {activeTabConfig?.label}
              </span>
            </div>
          </div>
          {(activeTabConfig?.count ?? 0) > 0 && (
            <RetryButton
              label="Retry all"
              disabled={isRetrying}
              onClick={handleBulkRetry}
            />
          )}
        </div>

        {isLoading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading failed operations…</p>
          </div>
        ) : (
          <>
            {activeTab === 'queue' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Queue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed at</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {queueJobs.length === 0 ? (
                      <EmptyRow message="No failed queue jobs." />
                    ) : (
                      queueJobs.map((job) => (
                        <tr key={job.uuid}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-50">{job.job_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{job.queue}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {job.failed_at ? (
                              <>
                                {formatDate(job.failed_at, 'medium')}
                                <span className="block text-xs text-gray-500">{formatTime(job.failed_at)}</span>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-red-700 dark:text-red-400 max-w-xs truncate" title={job.exception}>
                            {job.exception || '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <RetryButton
                              disabled={isRetrying}
                              onClick={() => retryQueueJob.mutate(job.uuid)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'outbox' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {outboxEvents.length === 0 ? (
                      <EmptyRow message="No failed outbox events." />
                    ) : (
                      outboxEvents.map((event) => (
                        <tr key={event.id}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-50">{event.event_type}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono">{event.topic}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 tabular-nums">{event.attempts}</td>
                          <td className="px-4 py-3 text-xs text-red-700 dark:text-red-400 max-w-xs truncate" title={event.last_error}>
                            {event.last_error || '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <RetryButton
                              disabled={isRetrying}
                              onClick={() => retryOutboxEvent.mutate(event.id)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'stripe' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stripe event ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {stripeWebhooks.length === 0 ? (
                      <EmptyRow message="No failed Stripe webhooks." />
                    ) : (
                      stripeWebhooks.map((webhook) => (
                        <tr key={webhook.id}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-50">{webhook.event_type}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono truncate max-w-[12rem]">{webhook.event_id}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {webhook.updated_at ? (
                              <>
                                {formatDate(webhook.updated_at, 'medium')}
                                <span className="block text-xs text-gray-500">{formatTime(webhook.updated_at)}</span>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-red-700 dark:text-red-400 max-w-xs truncate" title={webhook.error_message}>
                            {webhook.error_message || '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <RetryButton
                              disabled={isRetrying}
                              onClick={() => retryStripeWebhook.mutate(webhook.id)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {activeMeta?.last_page > 1 && (
        <Pagination
          currentPage={activeMeta.current_page}
          lastPage={activeMeta.last_page}
          perPage={activeMeta.per_page || perPage}
          total={activeMeta.total}
          onPageChange={handlePageChange}
          scrollToTop={false}
        />
      )}
    </div>
  );
};

export default AdminFailedOperations;
